import React, { useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectIsAdvancedModeEnabled } from "../../../store/app-configurations/app-configurations-selectors";
import { selectCurrentRobotState } from "../../../store/status/status-selectors";
import { selectDownloadFilesLink, selectRooms } from "../../../store/room/room-selectors";
import { FilterKeys } from "../../../enums/query-params/filters";
import MapQueryParams, { MapTypeParam } from "../../../enums/robot/mapping/mapping-query";
import WebAppNavigationRoutes from "../../../enums/query-params/webapp-navigation-routes";
import RswState from "../../../enums/robot/rsw/rsw-state";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import ConfirmAbortDialog from "../../General/ConfirmAbortDialog";
import { replaceArgsInMessage } from "../../../utils/strings";

const useStyles = makeStyles()((theme) => ({
  buttonLeftText: {
    justifyContent: "flex-start",
  },
}));

const ListedRoomActions: React.FC<{
  roomMetaInfo: RoomData;
  showTransitionMappingButton?: boolean;
  allowDelete: boolean;
}> = ({ roomMetaInfo, showTransitionMappingButton, allowDelete }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setQueryParams] = useSearchParams();

  const dispatch = useSharedDispatch();
  const navigate = useNavigate();

  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const currentRobotState = useSharedSelector((state) => selectCurrentRobotState(state));
  const availableRooms = useSharedSelector((state) => selectRooms(state));
  const downloadFilesLink = useSharedSelector((state) => selectDownloadFilesLink(state));

  const { patchRoomById, deleteRoomById } = useSharedActions();

  const [isDeleteRoomDialogOpen, setIsDeleteRoomDialogOpen] = useState(false);
  const [isArchiveRoomDialogOpen, setIsArchiveRoomDialogOpen] = useState(false);

  const downloadRoomFilesClickHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const deleteRoomClickHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsDeleteRoomDialogOpen(true);
  };

  const archiveRoomClickHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (roomMetaInfo.isArchived) {
      dispatch(patchRoomById({ id: roomMetaInfo.id, data: { id: roomMetaInfo.id, isArchived: false } }));
    } else {
      setIsArchiveRoomDialogOpen(true);
    }
  };

  const deleteRoomDialogAbortHandler = () => {
    setIsDeleteRoomDialogOpen(false);
  };

  const deleteRoomDialogConfirmHandler = () => {
    dispatch(deleteRoomById({ roomId: roomMetaInfo.id }));
    setQueryParams((prevState) => {
      prevState.set(
        FilterKeys.RoomId,
        [...availableRooms]
          .sort((a, b) => {
            if (a.secondaryStation != null) {
              return 1;
            }
            if (b.secondaryStation != null) {
              return -1;
            }
            return a.name > b.name ? 1 : -1;
          })
          .find((r) => r.isArchived === false && r.id !== roomMetaInfo.id)?.id ?? "",
      );
      return prevState;
    });
    setIsDeleteRoomDialogOpen(false);
  };

  const archiveRoomDialogAbortHandler = () => {
    setIsArchiveRoomDialogOpen(false);
  };

  const archiveRoomDialogConfirmHandler = () => {
    dispatch(patchRoomById({ id: roomMetaInfo.id, data: { id: roomMetaInfo.id, isArchived: true } }));
    setIsArchiveRoomDialogOpen(false);
  };

  const navigateToMappingHandler = (mappingType: MapTypeParam, includeRoomId?: boolean) => {
    let searchQuery = `?${FilterKeys.FloorId}=${roomMetaInfo.floorId}&${MapQueryParams.PrimaryStation}=${roomMetaInfo.primaryStation.stationId}&${MapQueryParams.MappingType}=${mappingType}`;
    if (includeRoomId) {
      searchQuery += `&${FilterKeys.RoomId}=${roomMetaInfo.id}`;
    }
    navigate({
      pathname: `${WebAppNavigationRoutes.routeRoomMapping}/`,
      search: searchQuery,
    });
  };

  const continueMappingClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigateToMappingHandler(MapTypeParam.ContinueMapping, true);
  };

  const redoMappingClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigateToMappingHandler(MapTypeParam.RedoMapping, true);
  };

  const transitionMappingClickHandler = () => {
    navigateToMappingHandler(MapTypeParam.TransitionMapping);
  };

  const continueMappingEnabled = roomMetaInfo.hasPosegraphAndDataFiles;

  const isRobotInIdleState = useMemo(
    () => currentRobotState?.status?.state === RswState.Idle || currentRobotState?.status?.state === RswState.Ready,
    [currentRobotState?.status?.state],
  );

  const mappingDisabledTooltip = isRobotInIdleState
    ? undefined
    : currentRobotState == null
      ? t("mapping-robot-unknown-state-error")
      : t("mapping-robot-not-idle-warning");

  
  const downloadRoomLink =
    downloadFilesLink == null ? null : replaceArgsInMessage(downloadFilesLink, [roomMetaInfo.id]);

  return (
    <React.Fragment>
      {roomMetaInfo.secondaryStation?.stationId == null && !roomMetaInfo.isArchived && (
        <React.Fragment>
          {showTransitionMappingButton && (
            <Tooltip title={mappingDisabledTooltip} placement="right">
              <span>
                <ReadOnlyProtectedButton
                  color="primary"
                  size="small"
                  fullWidth
                  disabled={!isRobotInIdleState}
                  className={classes.buttonLeftText}
                  onClick={transitionMappingClickHandler}
                >
                  {t("roommappingwizard-transition-mapping")}
                </ReadOnlyProtectedButton>
              </span>
            </Tooltip>
          )}
          <Tooltip
            placement="right"
            title={
              mappingDisabledTooltip != null
                ? mappingDisabledTooltip
                : continueMappingEnabled
                  ? undefined
                  : t("roommappingwizard-continue-mapping-missing-files")
            }
          >
            <span>
              <ReadOnlyProtectedButton
                color="primary"
                size="small"
                fullWidth
                disabled={!continueMappingEnabled || !isRobotInIdleState}
                className={classes.buttonLeftText}
                onClick={continueMappingClickHandler}
              >
                {t("roommappingwizard-continue-mapping")}
              </ReadOnlyProtectedButton>
            </span>
          </Tooltip>
          <Tooltip title={mappingDisabledTooltip} placement="right">
            <span>
              <ReadOnlyProtectedButton
                color="primary"
                size="small"
                fullWidth
                disabled={!isRobotInIdleState}
                className={classes.buttonLeftText}
                onClick={redoMappingClickHandler}
              >
                {t("roommappingwizard-label-re-mapping")}
              </ReadOnlyProtectedButton>
            </span>
          </Tooltip>
        </React.Fragment>
      )}

      {(isAdvancedModeEnabled || roomMetaInfo.isArchived) && (
        <React.Fragment>
          {roomMetaInfo.secondaryStation == null && downloadRoomLink != null && (
            <ReadOnlyProtectedButton
              id="downloadRoomFilesButton"
              color="primary"
              size="small"
              fullWidth
              className={classes.buttonLeftText}
              onClick={downloadRoomFilesClickHandler}
            >
              <Link href={downloadRoomLink} sx={{ textDecoration: "none" }}>
                {t("action-download-room-files")}
              </Link>
            </ReadOnlyProtectedButton>
          )}

          {roomMetaInfo.secondaryStation?.stationId == null && (
            <Tooltip title={!allowDelete ? t("info-tooltip-archive-disabled-in-floor") : undefined} placement="right">
              <span>
                <ReadOnlyProtectedButton
                  id="archiveRoomButton"
                  color="primary"
                  size="small"
                  fullWidth
                  className={classes.buttonLeftText}
                  onClick={archiveRoomClickHandler}
                  disabled={!allowDelete}
                >
                  {t(roomMetaInfo.isArchived ? "rooms-unarchive-room" : "rooms-archive-room")}
                </ReadOnlyProtectedButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={!allowDelete ? t("info-tooltip-delete-disabled-in-floor") : undefined} placement="right">
            <span>
              <ReadOnlyProtectedButton
                id="deleteRoomButton"
                color="secondary"
                size="small"
                fullWidth
                className={classes.buttonLeftText}
                onClick={deleteRoomClickHandler}
                disabled={!allowDelete}
              >
                {t("rooms-delete-room")}
              </ReadOnlyProtectedButton>
            </span>
          </Tooltip>
        </React.Fragment>
      )}
      {isDeleteRoomDialogOpen && (
        <ConfirmAbortDialog
          isOpen={isDeleteRoomDialogOpen}
          title={t("rooms-delete-room")}
          description={t("rooms-delete-room-description")}
          confirmButtonText={t("button-confirm")}
          abortButtonText={t("button-abort")}
          onAbort={deleteRoomDialogAbortHandler}
          onConfirm={deleteRoomDialogConfirmHandler}
        />
      )}
      {isArchiveRoomDialogOpen && (
        <ConfirmAbortDialog
          isOpen={isArchiveRoomDialogOpen}
          title={t("rooms-archive-room")}
          description={t("rooms-archive-room-description")}
          confirmButtonText={t("button-confirm")}
          abortButtonText={t("button-abort")}
          onAbort={archiveRoomDialogAbortHandler}
          onConfirm={archiveRoomDialogConfirmHandler}
        />
      )}
    </React.Fragment>
  );
};

export default ListedRoomActions;
